"use client";

/**
 * CollabV3FeeSessions 컴포넌트
 * @description 회차 카드의 요약(`2 sessions • next 16 October 18:00 • 9 seats left`)과 날짜별 회차 줄
 *   (앱 `CollabFeeBlock` 의 회차 자리). 줄의 상태는 앱 규칙과 같다 — 취소 `Cancelled`, 마감 `Closed`,
 *   꽉 참 `Full`, 그 밖엔 남은 자리. 취소 · 마감 · 꽉 참은 흐리게.
 * @example <CollabV3FeeSessions sessions={detail.fee.sessions} />
 */
import { memo, useMemo } from "react";

import { collabV3CardStyles } from "./CollabV3Card.styles";
import type { CollabV3FeeSessionsProps } from "./CollabV3Card.types";
import { useCollabV3TimeZone } from "./CollabV3TimeZone";

import { COLLAB_V3_CARD_COPY } from "../../constants/collabV3CardCopy";
import type { CollabV3FeeSession } from "../../entities";
import { groupSessionsByDay, summarizeSessions } from "../../utils/collabV3Sessions";

const copy = COLLAB_V3_CARD_COPY.fee;

function stateOf(session: CollabV3FeeSession): string {
  if (session.isCancelled) return copy.badge.cancelled;
  if (session.isClosed) return copy.sessions.closed;
  if (session.remainingSeats <= 0) return copy.seatFull;
  return copy.seatsLeft(session.remainingSeats);
}

const isMuted = (session: CollabV3FeeSession): boolean =>
  session.isCancelled || session.isClosed || session.remainingSeats <= 0;

const CollabV3FeeSessions = memo<CollabV3FeeSessionsProps>(({ sessions }) => {
  const styles = collabV3CardStyles.sessions;
  const timeZone = useCollabV3TimeZone();
  const summary = useMemo(
    () => summarizeSessions(sessions, timeZone),
    [sessions, timeZone],
  );
  const groups = useMemo(
    () => groupSessionsByDay(sessions, timeZone),
    [sessions, timeZone],
  );

  return (
    <div className={collabV3CardStyles.stack}>
      <p className={collabV3CardStyles.body}>{summary}</p>
      {groups.map((group) => (
        <div key={group.key} className={styles.group}>
          <p className={styles.date}>{group.label}</p>
          {group.rows.map(({ session, time }) => (
            <p
              key={session.id}
              className={`${styles.row} ${isMuted(session) ? styles.muted : ""}`}
            >
              <span className={styles.time}>{time}</span>
              <span className={styles.seats}>{stateOf(session)}</span>
            </p>
          ))}
        </div>
      ))}
    </div>
  );
});

CollabV3FeeSessions.displayName = "CollabV3FeeSessions";

export default CollabV3FeeSessions;
